import { create } from 'zustand';
import { adminApi } from '../services/api';

export const useAuthStore = create((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  setupRequired: false,
  
  // Check if initial setup is needed
  checkSetup: async () => {
    try {
      const response = await adminApi.checkSetupRequired();
      set({ setupRequired: response.data.setupRequired });
      return response.data.setupRequired;
    } catch (error) {
      console.error('Setup check error:', error);
      return false;
    }
  },
  
  // Initial setup
  initialSetup: async (data) => {
    try {
      await adminApi.initialSetup(data);
      set({ setupRequired: false });
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Setup failed' 
      };
    }
  },
  
  // Login
  login: async (email, password) => {
    try {
      const response = await adminApi.login({ email, password });
      const { user, accessToken, refreshToken } = response.data;
      
      // Check if user has admin role
      if (!['SUPER_ADMIN', 'MANAGING_ADMIN'].includes(user.role)) {
        return { 
          success: false, 
          error: 'Access denied. Admin privileges required.' 
        };
      }
      
      // Store tokens
      localStorage.setItem('admin_token', accessToken);
      localStorage.setItem('admin_refresh_token', refreshToken);
      
      set({
        user,
        isAuthenticated: true,
        isLoading: false
      });
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Login failed' 
      };
    }
  },
  
  // Logout
  logout: async () => {
    try {
      await adminApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_refresh_token');
      set({
        user: null,
        isAuthenticated: false
      });
    }
  },
  
  // Fetch current user
  fetchUser: async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      set({ isLoading: false });
      return;
    }
    
    try {
      const response = await adminApi.getMe();
      const { user } = response.data;
      
      // Verify admin role
      if (!['SUPER_ADMIN', 'MANAGING_ADMIN'].includes(user.role)) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_refresh_token');
        set({ user: null, isAuthenticated: false, isLoading: false });
        return;
      }
      
      set({
        user,
        isAuthenticated: true,
        isLoading: false
      });
    } catch (error) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_refresh_token');
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false
      });
    }
  },
  
  // Initialize
  initialize: async () => {
    const { checkSetup, fetchUser, isAuthenticated } = get();
    
    // Skip if already authenticated
    if (isAuthenticated) {
      set({ isLoading: false });
      return;
    }
    
    const needsSetup = await checkSetup();
    
    if (needsSetup) {
      set({ isLoading: false });
    } else {
      await fetchUser();
    }
  }
}));

